// type: pixel scale (paper notes over the world; see ui/kit.css)
// Slim dismissible banner shown on phones recommending a computer.
//
// The full experience, live voice argument with push-to-talk before nine
// justices, is built for a desk: a real microphone, SPACE-to-speak, and a
// bench that doesn't fit a 390px viewport. Beta drop-off analysis
// (2026-07-15) showed a phone user reaching the courtroom and bouncing in
// under a minute; this sets expectations up front instead. Static (pushes
// content, never overlays courtroom controls) and remembered per tab
// session once dismissed.
import { useLayoutEffect, useRef, useState } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { MQ_MOBILE } from '../../lib/breakpoints';

const KEY = 'scotus.mobile_notice_dismissed';

/**
 * How tall this banner currently is, published to the document so the
 * full-screen layout roots can subtract it.
 *
 * The banner is a static sibling ABOVE every route, so it pushes whatever
 * follows down the page. That was harmless while the roots were min-height
 * 100vh (already taller than a phone shows). Now that they are 100dvh, sized
 * to exactly what the phone shows, a banner above them pushes precisely its
 * own height of content below the fold, and on the argument route the thing
 * at the bottom is the Speak dock. The banner's own docstring promises it
 * "never overlays courtroom controls"; pushing them off the screen is the
 * same broken promise by another route.
 *
 * Measured from the rendered element rather than computed from the padding
 * and font size, because the real number depends on font metrics and on
 * whether the sentence wraps: a tester measured 58px where the declared
 * values add up to about 49.
 */
const HEIGHT_VAR = '--chrome-notice-h';

function isSmallTouchScreen(): boolean {
  try {
    return (
      window.matchMedia(MQ_MOBILE).matches &&
      window.matchMedia('(pointer: coarse)').matches
    );
  } catch {
    return false;
  }
}

/**
 * The live argument is the one route where the banner has no work to do (the
 * player already chose to play) and the one where its height costs the most:
 * it is added to the room window, so on a 360x740 phone its two lines took the
 * paper, the answer box and the Speak button out from under the desk row.
 */
const GAME_PATH = /^\/play\//;

function usePathname(): string {
  // Constant for the life of a mount, so the hook order never changes.
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return inRouter ? useLocation().pathname : window.location.pathname;
}

export default function MobileNotice() {
  const onGame = GAME_PATH.test(usePathname());
  const [shown, setShown] = useState<boolean>(() => {
    try {
      return isSmallTouchScreen() && sessionStorage.getItem(KEY) !== '1';
    } catch {
      return false;
    }
  });
  const visible = shown && !onGame;
  const ref = useRef<HTMLElement>(null);

  // Publish the height while the banner is up, and take it away the moment it
  // is dismissed or unmounted, so the roots get their whole viewport back.
  // The cleanup is the same closure that sets it, so there is no second call
  // site to forget: a stale value here would silently shorten every
  // full-screen root on the site.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!visible) {
      root.style.removeProperty(HEIGHT_VAR);
      return;
    }
    const publish = () => {
      const h = ref.current?.offsetHeight ?? 0;
      root.style.setProperty(HEIGHT_VAR, `${h}px`);
    };
    publish();
    // The sentence wraps to two lines on a narrow enough phone, which changes
    // the height without anything re-rendering. ResizeObserver is missing in
    // jsdom and in older browsers, and its absence costs only the re-measure,
    // not the initial one.
    const RO = window.ResizeObserver;
    const ro = RO && ref.current ? new RO(publish) : null;
    if (ro && ref.current) ro.observe(ref.current);
    return () => {
      ro?.disconnect();
      root.style.removeProperty(HEIGHT_VAR);
    };
  }, [visible]);

  if (!visible) return null;
  return (
    // An <aside> with a name, rather than a bare role="note", so the banner
    // sits in a landmark of its own (axe "region") on Landing and the share
    // page, which have no shell around them.
    <aside
      ref={ref}
      aria-label="Device notice"
      style={{
        // Positioned with a z-index of its own. As a plain static block it had
        // no stacking context, so the shell's fixed, opaque .writ-bg (z 0),
        // which comes after it in the document, painted straight over it: on
        // every shell route the sentence and the dismiss button were a blank
        // 49px strip that still took taps.
        position: 'relative',
        // Above the HUD, which moves down by this banner's height (--chrome-notice-h) while it is shown.
        zIndex: 45,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        // A note slipped under the door: ruled paper, a brass tack, its shadow cast down onto the room below.
        padding: 'var(--sp-2) var(--sp-3) var(--sp-2) 26px',
        background: '#fffbf2',
        backgroundImage: 'repeating-linear-gradient(180deg, transparent 0 21px, rgba(86, 80, 104, .14) 21px 22px)',
        borderBottom: '2px solid #160f1a',
        boxShadow: 'inset 0 -2px 0 #e7dbbf, 0 4px 0 rgba(22, 15, 26, .45)',
        color: '#3c3749',
        fontFamily: 'var(--f-text)',
        fontSize: 'var(--fs-2)',
        lineHeight: 'var(--lh-snug)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute', left: 9, top: 12, width: 6, height: 6, background: '#c59b3c',
          boxShadow: 'inset 2px 2px 0 #eed47c, 0 0 0 2px #160f1a',
        }}
      />
      <span style={{ flex: 1 }}>
        Best on a computer. On a phone you can argue aloud or type every answer.
      </span>
      <button
        aria-label="Dismiss"
        onClick={() => {
          try { sessionStorage.setItem(KEY, '1'); } catch { /* ignore */ }
          setShown(false);
        }}
        style={{
          background: 'transparent',
          border: 0,
          color: 'var(--ink-2)',
          cursor: 'pointer',
          fontSize: 'var(--fs-2)',
          // 44x44 minimum. This control only ever renders on a phone, and it
          // was a 19x23 target, the one place the excuse does not survive.
          // Negative margin keeps the banner the same height it always was.
          padding: 'var(--sp-3)',
          margin: -6,
          minWidth: 44,
          minHeight: 44,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        ✕
      </button>
    </aside>
  );
}
